'use client'

import type { Partner, Footer } from '@/payload-types'
import type { PartnerDomainMapping } from '@/types/partnerMappingType'
import { ClientFooter } from '@/website/components/ClientFooter'
import { ClientPartnerStyling } from '@/website/components/ClientPartnerStyling'
import { ClientHeader } from '@/website/components/ClientHeader'
import { cn } from '@/website/utils/tailwindUtils'
import Script from 'next/script'
import { useContext, useEffect, useState } from 'react'
import { getCollectionItem } from '@/website/utils/getCollection'
import { ClientSidePartnerContext } from '@/website/context/clientSidePartnerContext'

type Props = {
    partner: Partner;
    children: React.ReactNode;
    footer: Footer;
    defaultPartnerId: string;
};

export const PageLayout = (props: Props) => {
    const { partner, children, footer, defaultPartnerId } = props
    const [isLoading, setIsLoading] = useState(false)
    const { setClientSidePartner, clientSidePartner } = useContext(ClientSidePartnerContext)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const host = window.location.hostname
                const resJson = await fetch(`${process.env.NEXT_PUBLIC_SERVER_URL}/partners.json`)
                const partnerDominMappings: PartnerDomainMapping[] = await resJson.json()
                const foundMapping = partnerDominMappings.find(
                    (mapping) => mapping.domains.length && mapping.domains.includes(host),
                )
                if (foundMapping) {
                    const derivedPartner = await getCollectionItem<Partner>('partners', { slug: { equals: foundMapping.slug }})
                    if (derivedPartner) {
                        if (partner.slug !== derivedPartner.slug) {
                            setClientSidePartner(derivedPartner)
                        }
                    }
                }
                setIsLoading(false)
            } catch (error) {
                console.error("Failed to fetch data: ", error);
                setIsLoading(false)
            }
        }

        
        // Check for the partner param present in URL params
        const partnerParam =  new URLSearchParams(location.search).get('partner')
        if (!partnerParam) {
            fetchData()
        } else {
            setIsLoading(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

	return isLoading ? null : (
		<>
			{((clientSidePartner.backgroundImage ?? "") !== "" || clientSidePartner.colors || clientSidePartner.buttonBorder || clientSidePartner.gameTile) && 
				<ClientPartnerStyling
					{...clientSidePartner}
				/>
			}
			{(clientSidePartner.customCss ?? "") !== "" && <style>{`${clientSidePartner.customCss}`}</style>}
			{clientSidePartner.organisationSchema && (
				<Script
					id="organisationSchema"
					strategy="beforeInteractive"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ 
						__html: `{"@context": "https://schema.org","@graph": [${clientSidePartner.organisationSchema}]}`
					}}
				/>
			)}
			<ClientHeader
				logo={clientSidePartner.logo}
				link={clientSidePartner.website}
				hidePartnerButton={clientSidePartner.id !== defaultPartnerId}
                isDefaultPartner={defaultPartnerId === clientSidePartner.id}
			/>
			<main className={cn('grow')}>
				{children}
			</main>
			<ClientFooter partner={clientSidePartner} footer={footer} isDefaultPartner={defaultPartnerId === clientSidePartner.id} />
		</>
	)
}
